import { z } from 'zod';

/**
 * As telas que cada usuário marcou como favoritas no menu (`.spec/015`,
 * decisão 75).
 *
 * **O que se grava é a cota, e só ela.** A cota (`CMP·03`) é o endereço da
 * tela — o suporte a cita ao telefone — e por isso nunca se renumera nem se
 * reaproveita (`apps/web/src/shared/lib/cotas.ts`). É a única chave que
 * sobrevive a uma troca de rótulo ou de rota sem invalidar o favorito de
 * ninguém. Gravar a rota foi descartado por isso: `/notas-compra/por-pedido`
 * já mudou de nome uma vez.
 *
 * **O servidor não conhece o catálogo.** O mapa de telas e o menu vivem no
 * `apps/web`, e a API confere só o **formato** da cota. Uma cota que deixe de
 * existir continua gravada e é ignorada pela tela na leitura — o recorte por
 * papel funciona do mesmo jeito: favorito de tela que o papel perdeu some do
 * menu sem que ninguém precise apagá-lo, e volta se o papel voltar.
 *
 * **A ordem é a do vetor.** Desde a decisão 105 o menu de Favoritos é uma
 * lista só, com as telas de todos os módulos, e a mostra como está aqui. Até
 * ali o menu agrupava os favoritos pelo módulo de cada tela e respeitava,
 * dentro do módulo, a posição relativa — o vetor já era um só, e por isso a
 * mudança não pediu migração.
 */

/** `MOD·NN` colado — o mesmo formato que `cotas.test.ts` cobra das páginas. */
export const FORMATO_COTA = /^[A-Z]{3}·\d{2}$/u;

/**
 * Teto de favoritos por usuário.
 *
 * Folgado de propósito: hoje o catálogo inteiro tem menos de cinquenta telas,
 * e marcar todas é permitido — é só um menu sem submenus. O teto existe para
 * que um cliente com defeito não grave um vetor de dez mil posições.
 */
export const MAXIMO_FAVORITOS = 100;

export const cotaSchema = z
  .string()
  .regex(FORMATO_COTA, 'Cota fora do formato MOD·NN (ex.: CMP·03).');

export const menuFavoritosSchema = z
  .object({
    /**
     * As cotas na ordem em que aparecem no menu. Repetir uma cota é recusado:
     * duas posições para a mesma tela deixariam a ordem ambígua.
     */
    cotas: z
      .array(cotaSchema)
      .max(MAXIMO_FAVORITOS, `No máximo ${String(MAXIMO_FAVORITOS)} favoritos.`)
      .superRefine((cotas, ctx) => {
        const vistas = new Set<string>();
        cotas.forEach((cota, indice) => {
          if (vistas.has(cota)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [indice],
              message: `A tela ${cota} aparece duas vezes nos favoritos.`,
            });
          }
          vistas.add(cota);
        });
      }),
  })
  .strict();

export type MenuFavoritos = z.infer<typeof menuFavoritosSchema>;

/* ------------------------------ Regras puras ------------------------------ */

/**
 * Liga ou desliga uma tela nos favoritos.
 *
 * A tela nova entra **no fim**: é a posição que não reordena o que o usuário
 * já arrumou. Desligar remove sem mexer na ordem dos demais.
 */
export function alternarFavorito(cotas: readonly string[], cota: string): string[] {
  return cotas.includes(cota) ? cotas.filter((c) => c !== cota) : [...cotas, cota];
}

/**
 * Move um favorito uma posição **dentro do grupo** a que ele pertence.
 *
 * O grupo é o que o usuário vê numa lista só. Com o menu de Favoritos (decisão
 * 105), é o vetor inteiro, e a tela passa `() => true`. O parâmetro ficou
 * porque foi escrito para o menu por módulo, em que trocar com o vizinho do
 * vetor global faria a seta não produzir efeito quando o vizinho fosse de
 * outro módulo — e é o que o teste desta função ainda cobre.
 *
 * `mesmoGrupo` diz quais cotas são do grupo da que se move. Sem vizinho na
 * direção pedida — primeiro que sobe, último que desce —, devolve o vetor igual.
 */
export function moverFavorito(
  cotas: readonly string[],
  cota: string,
  direcao: 'subir' | 'descer',
  mesmoGrupo: (outra: string) => boolean,
): string[] {
  const posicao = cotas.indexOf(cota);
  if (posicao === -1) return [...cotas];

  const passo = direcao === 'subir' ? -1 : 1;
  let vizinho = posicao + passo;
  while (vizinho >= 0 && vizinho < cotas.length) {
    const candidata = cotas[vizinho];
    if (candidata !== undefined && mesmoGrupo(candidata)) break;
    vizinho += passo;
  }

  if (vizinho < 0 || vizinho >= cotas.length) return [...cotas];

  const resultado = [...cotas];
  const outra = resultado[vizinho];
  if (outra === undefined) return resultado;
  resultado[vizinho] = cota;
  resultado[posicao] = outra;
  return resultado;
}
